import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { TranslateContext } from "../../context/TranslateContext";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import Content from "./Content";
import { GlobalContext } from "../../context/GlobalContext";
import { useMenuItems } from "./useMenuItems";
import { ThemeDataContext } from "../../context/ThemeContext";

const DRAWER_WIDTH = 260;

const AdminDashboard = () => {
  const { lang } = React.useContext(TranslateContext);
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const { toggleColorMode, isDark } = React.useContext(ThemeDataContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const menuItems = useMenuItems();

  const getInitialMenu = () => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = params.get("page");
    if (pageParam) {
      const isValid = menuItems.some(
        (item) =>
          item.id === pageParam ||
          item.submenu?.some((sub) => sub.id === pageParam),
      );
      if (isValid) return pageParam;
    }
    return menuItems[0]?.submenu?.[0]?.id || menuItems[0]?.id || "dashboards";
  };

  const [selectedMenu, setSelectedMenu] = React.useState(getInitialMenu);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [openSubmenus, setOpenSubmenus] = React.useState(() => {
    const openMenus = {};
    menuItems.forEach((item) => {
      if (item.submenu) openMenus[item.id] = true;
    });
    return openMenus;
  });

  const updateURL = (menuId) => {
    const params = new URLSearchParams(window.location.search);
    params.set("page", menuId);
    window.history.pushState({}, "", `${window.location.pathname}?${params}`);
  };

  const handleDrawerToggle = () => setMobileOpen((p) => !p);

  const handleMenuClick = (menuId, hasSubmenu) => {
    if (hasSubmenu) {
      setOpenSubmenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    } else {
      setSelectedMenu(menuId);
      updateURL(menuId);
      if (isMobile) setMobileOpen(false);
    }
  };

  const handleSubmenuClick = (submenuId) => {
    setSelectedMenu(submenuId);
    updateURL(submenuId);
    if (isMobile) setMobileOpen(false);
  };

  React.useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const pageParam = params.get("page");
      if (pageParam) {
        setSelectedMenu(pageParam);
        menuItems.forEach((item) => {
          if (item.submenu?.some((sub) => sub.id === pageParam)) {
            setOpenSubmenus((prev) => ({ ...prev, [item.id]: true }));
          }
        });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [menuItems]);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Sidebar
        lang={lang}
        isMobile={isMobile}
        selectedMenu={selectedMenu}
        openSubmenus={openSubmenus}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        handleMenuClick={handleMenuClick}
        handleSubmenuClick={handleSubmenuClick}
        menuItems={menuItems}
        drawerWidth={DRAWER_WIDTH}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: { xs: 7, md: 0 },
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <TopBar
          lang={lang}
          isMobile={isMobile}
          isDark={isDark}
          selectedMenu={selectedMenu}
          toggleColorMode={toggleColorMode}
          handleDrawerToggle={handleDrawerToggle}
        />

        <Box sx={{ p: { xs: 2, sm: 3 }, flexGrow: 1 }}>
          <Content selectedMenu={selectedMenu} menuItems={menuItems} />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
